import { AttachMoney, Title as TIcon } from "@mui/icons-material";
import {
	Box,
	Chip,
	Icon,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { differenceInDays, format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import React, { useEffect, useState } from "react";
import { StyledBadge } from "../../../../infra/components/StyleBadge/StyledBadge";
import { HtmlTooltip } from "../../../../infra/components/tooltipsCustom/tooltipsCuston";
import { ToolbarGridProposal } from "../../../../shared/components";
import { Environment } from "../../../../shared/environment";
import { globalbackgroundProposal, globalColorTextProposal, globalMaskDocument, globalTranslate, globalTranslateStatus } from "../../../../shared/utils";
import { renderCancellationStatus, renderRenovationStatus } from "../../helps/help";

interface TableProposalProps {
	rows: any[];
	isLoading: boolean;
	totalCount: number;
	pagina: number;
	busca: string;
	typeSearch: string;
	statusProposal: string;
	origemProposal: string;
	dataIniProposal: string;
	dataFimProposal: string;
	clientFilter: string;
	managerId: string;
	tombamento: string;
	setSearchParams: (params: any, options: any) => void;
	limit: string;
}

const TableProposal: React.FC<TableProposalProps> = ({
	rows,
	isLoading,
	totalCount,
	pagina,
	busca,
	typeSearch,
	statusProposal,
	origemProposal,
	dataIniProposal,
	dataFimProposal,
	clientFilter,
	managerId,
	tombamento,
	setSearchParams,
	limit,
}) => {
	const URL = window.location.protocol + "//" + window.location.host;
	const [filterDescription, setFilterDescription] = useState("");
	const rowsPerPage = Number(limit) || 10;
	const isExpiredGroup = ["expired", "expired_renovated", "expired_not_renovated"].includes(statusProposal);
	const isSuspendedPending = statusProposal === "suspendedpending";
	const isSuspendedNotPending = statusProposal === "suspendednotpending";

	const handleFilterDescription = (description: string) => {
		const diffInDays = differenceInDays(new Date(dataFimProposal), new Date(dataIniProposal));
		const newEndDate = format(new Date(new Date().setDate(new Date().getDate() + diffInDays)), "dd/MM/yyyy");

		setFilterDescription(
			statusProposal != "expires"
				? `Filtro:  Garantias ${description} no período de ${format(parseISO(dataIniProposal), "dd/MM/yyyy")} a ${format(
					parseISO(dataFimProposal), //implementar o + " 03:00:00" no final caso precise
					"dd/MM/yyyy"
				)}`
				: diffInDays != 0
					? `Filtro:  Garantias ${description} no período de ${format(new Date(), "dd/MM/yyyy")} a ${newEndDate} - próximos ${diffInDays} dias`
					: `Filtro:  Garantias ${description} no período de ${format(new Date(), "dd/MM/yyyy")} a ${newEndDate} - hoje`
		);
	};

	useEffect(() => {
		switch (statusProposal) {
			case "Todos":
				handleFilterDescription("criadas");
				break;
			case "active":
				handleFilterDescription("iniciadas");
				break;
			case "emited":
				handleFilterDescription("contratadas");
				break;
			case "expires":
				handleFilterDescription("a expirar");
				break;
			case "expired":
				handleFilterDescription("expiradas");
				break;
			case "expired_renovated":
				handleFilterDescription("expiradas e renovadas");
				break;
			case "expired_not_renovated":
				handleFilterDescription("expiradas e não renovadas");
				break;
			case "canceled":
				handleFilterDescription("canceladas");
				break;
			case "suspended":
				handleFilterDescription("suspensas");
				break;
			case "renovated":
				handleFilterDescription("renovadas");
				break;
			case "renovation":
				handleFilterDescription("em renovação");
				break;
			case "suspendednotpending":
				handleFilterDescription("suspensas");
				break;
			case "suspendedpending":
				handleFilterDescription("suspensas e pendentes");
				break;
			default:
				handleFilterDescription("criadas");
				break;
		}
	}, [statusProposal, dataIniProposal, dataFimProposal]);

	function handleStatusChange(status: string): void {
		setSearchParams(
			{
				busca,
				pagina: "1",
				tipo: typeSearch,
				status,
				origem: origemProposal,
				dataini: dataIniProposal,
				datafim: dataFimProposal,
				clientFilter: clientFilter,
				managerId: managerId,
				tombamento: tombamento,
				limit: limit,
			},
			{ replace: true }
		);
	}

	// ==========================================================
	// FUNÇÕES AUXILIARES (para garantir que estejam disponíveis)
	// ==========================================================

	// 1. Monta o endereço completo
	const buildFullAddress = (row: any): string => {
		const street = row.addressStreet || "";
		const number = row.addressNumber || "";
		const complement = row.addressComplement && row.addressComplement.trim() !== "" ? ` - ${row.addressComplement}` : "";
		const district = row.addressDistrict || "";

		if (!street) return "Endereço não cadastrado";

		const streetAndNumber = `${street}, ${number}`;
		const districtPart = district ? ` - ${district}` : "";

		return `${streetAndNumber}${complement}${districtPart}`;
	};

	// 2. Trunca o texto para exibição
	const truncateText = (text: string, limit: number = 50): string => {
		if (text.length <= limit) return text;
		return text.substring(0, limit) + "...";
	};

return (
	<TableContainer
		component={Paper}
		variant="elevation"
		sx={{
			mt: 1,
			width: "96%",
			px: { xs: 1, sm: 3 },
			py: { xs: 2, sm: 3 },
			ml: 1,
			borderRadius: 2,
			overflow: "hidden",
		}}
	>
		<Box mb={2}>
			<ToolbarGridProposal
				onClickAll={() => handleStatusChange("Todos")}
				onClickActive={() => handleStatusChange("active")}
				onClickEmitidas={() => handleStatusChange("emited")}
				onClickExpires={() => handleStatusChange("expires")}
				onClickExpired={() => handleStatusChange("expired")}
				onClickExpiredRenovated={() => handleStatusChange("expired_renovated")}
				onClickExpiredNotRenovated={() => handleStatusChange("expired_not_renovated")}
				onClickCanceled={() => handleStatusChange("canceled")}
				onClickSuspended={() => handleStatusChange("suspended")}
				onClickSuspendedPending={() => handleStatusChange("suspendedpending")}
				onClickSuspendedNotPending={() => handleStatusChange("suspendednotpending")}
				onClickRenovated={() => handleStatusChange("renovated")}
				onClickRenovation={() => handleStatusChange("renovation")}
				selectAll={statusProposal === "Todos"}
				selectActive={statusProposal === "active"}
				selectEmitidas={statusProposal === "emited"}
				selectRenovated={statusProposal === "renovated"}
				selectExpires={statusProposal === "expires"}
				selectExpired={isExpiredGroup}
				selectSuspended={statusProposal === "suspended"}
				selectCanceled={statusProposal === "canceled"}
				selectRenovation={statusProposal === "renovation"}
				selectSupendedPending={isSuspendedPending}
				selectSupendedNotPending={isSuspendedNotPending}
				showFilterDescription={Boolean(filterDescription)}
				filterdescription={filterDescription}
			/>
		</Box>
		<Table
			size="small"
			stickyHeader
			aria-label="a dense table"
			sx={{
				"& .MuiTableCell-head": {
					backgroundColor: "grey.100",
					color: "text.secondary",
					borderBottomColor: "#f1f1f1",
				},
				"& .MuiTableCell-root": {
					borderBottomColor: "#f1f1f1",
				},
				"& tbody .MuiTableCell-root": {
					py: 0.5,
				},
			}}
		>
			<TableHead>
				<TableRow sx={{ backgroundColor: "grey.100" }}>
					<TableCell>
						<Typography variant="button" color="text.secondary">
							Cadastro
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="button" color="text.secondary">
							Inquilino
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="button" color="text.secondary">
							Imobiliária
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="button" color="text.secondary">
							Endereço
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="button" color="text.secondary">
							Parcela
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="button" color="text.secondary">
							Status
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="button" color="text.secondary">
							Vigência
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="button" color="text.secondary">
							Origem
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="button" color="text.secondary">
							#
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="button" color="text.secondary">
							Ações
						</Typography>
					</TableCell>
				</TableRow>
			</TableHead>

				<TableBody>
					{!isLoading &&
						rows.map((row) => (
							<TableRow key={row.id} hover>
								<TableCell>
									<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"} textAlign={"center"}>
										{row.createdAt ? `${format(new Date(row.createdAt), "dd/MM/yyyy", { locale: ptBR })}` : "N/A"}
									</Typography>

									<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"} textAlign={"center"}>
										{row.createdAt ? `${format(new Date(row.createdAt), "HH:mm:ss", { locale: ptBR })}` : "N/A"}
									</Typography>
								</TableCell>
								<TableCell sx={{ fontWeight: "normal" }}>
									{row.name}
									<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
										{globalMaskDocument(row.document)}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="subtitle2" color={"#5c5858"} sx={{ fontWeight: "normal" }}>
										{row.client?.name}
										{row.client?.status == "inactive" && " (Inativo)"}
									</Typography>
								</TableCell>

								<TableCell sx={{ color: "#959595", fontWeight: "normal" }}>
									<HtmlTooltip
										title={
											<React.Fragment>
												<Typography color="inherit" variant="body1" fontWeight={"500"}>
													Endereço Completo
												</Typography>
												<Typography color="inherit" variant="caption">
													{buildFullAddress(row)}
												</Typography>
											</React.Fragment>
										}
										disableHoverListener={buildFullAddress(row) === "Endereço não cadastrado" || buildFullAddress(row).length <= 50}
										placement="top"
										arrow
									>
										<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"} sx={{ cursor: "default" }}>
											{truncateText(buildFullAddress(row))}
										</Typography>
									</HtmlTooltip>

									<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
										{row.addressCity != null && row.addressCity != "" ? `${row.addressCity} - ${row.addressState}` : ""}
									</Typography>
									<Box display={"flex"} justifyContent={"start"} gap={5}>
										<Typography variant="caption" fontWeight={"bold"} color={"GrayText"}>
											{row.propertyFinally != undefined ? globalTranslate(row.propertyFinally) : ""}
										</Typography>
										<Typography variant="caption" fontWeight={"bold"} color={"GrayText"}>
											Certificado : {row.certificateNumber}
										</Typography>
									</Box>
								</TableCell>

								<TableCell>
									<Typography variant="subtitle2" color={"#959595"} sx={{ fontWeight: "normal", textAlign: "center" }}>
										({row.rentMonthsCount}X)
									</Typography>
									<Typography variant="subtitle2" color={"#5c5858"} sx={{ fontWeight: "normal", textAlign: "center" }}>
										{parseFloat(`${row?.assuranceMonthAmount}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
									</Typography>
								</TableCell>
								<TableCell sx={{ textAlign: "start" }}>
									<HtmlTooltip
										disableHoverListener={row.status == "active" && row.renovatedId ? false : true}
										title={
											<React.Fragment>
												<Typography color="inherit" variant="body1" fontWeight={"500"}>
													Garantia Renovada
												</Typography>
												<Typography color="inherit" variant="caption">
													Renovada em : {row.createdAt ? `${format(new Date(row.createdAt), "dd/MM/yyyy")}` : "N/A"}
												</Typography>
											</React.Fragment>
										}
									>
										<StyledBadge
											badgeContent={
												row.status === "active" && row.renovatedId
													? "R-1" // Exibe "R-1" se status for "active" e houver renovatedId
													: row.status === "suspended" && row.substatus === "pending"
														? "P-1" // Exibe "P-1" se status for "suspended" e submenu for "pending"
														: ""
											}
											color="secondary"
											invisible={
												!((row.status === "active" && row.renovatedId) || (row.status === "suspended" && row.substatus === "pending"))
											}
										>
											<Chip
												size="medium"
												label={
													row.status == "active" &&
														row.renovatedId &&
														differenceInDays(
															new Date(row.termBegin ? row.termBegin.substring(0, 10) + " 03:00:00" : "2021-01-01 03:00:01"),
															new Date().toISOString().substring(0, 10) + " 03:00:00"
														) > 0
														? ` Em Renovação `
														: ` ${globalTranslateStatus(row.status)}`
												}
												sx={{
													color: globalColorTextProposal(
														row.status == "active" &&
															row.renovatedId &&
															differenceInDays(
																new Date(row.termBegin ? row.termBegin.substring(0, 10) + " 03:00:00" : "2021-01-01 03:00:01"),
																new Date().toISOString().substring(0, 10) + " 03:00:00"
															) > 0
															? "renovated"
															: row.status
													),
													fontWeight: "500",
													background: globalbackgroundProposal(
														row.status == "active" &&
															row.renovatedId &&
															differenceInDays(
																new Date(row.termBegin ? row.termBegin.substring(0, 10) + " 03:00:00" : "2021-01-01 03:00:01"),
																new Date().toISOString().substring(0, 10) + " 03:00:00"
															) > 0
															? "renovated"
															: row.status
													),
													border: "0.5px solid #f1f1f1",
												}}
											/>
										</StyledBadge>
									</HtmlTooltip>

									<Typography variant="subtitle2" color={"#fd4b4b"} fontWeight={"normal"}>
										{renderRenovationStatus(row)}
										<Typography variant="subtitle2" color={"#fd4b4b"} fontWeight={"normal"}>
											{renderCancellationStatus(row)}
										</Typography>
									</Typography>
								</TableCell>

								<TableCell>
									<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
										<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
											{row.termBegin ? `${format(new Date(row.termBegin.substring(0, 10) + " 03:00:01"), "dd/MM/yyyy")}` : "N/A"}
										</Typography>
										<Typography variant="subtitle2" color={"#f83434"} fontWeight={"normal"}>
											{row.expiresAt ? `${format(new Date(row.expiresAt.substring(0, 10) + " 03:00:01"), "dd/MM/yyyy")}` : "N/A"}
										</Typography>
									</Typography>
								</TableCell>

								<TableCell sx={{ textAlign: "start" }}>
									<IconButton
										size="small"
										sx={{
											borderRadius: "18px",
											paddingX: "15px",
											paddingY: "1px",
											marginBottom: "5px",
										}}
									>
										{row.createdFrom == "web_app" ? (
											<img src="/logogar.png" alt="web_app" width={30} />
										) : (
											<img src="/4seg.png" alt="web_app" width={40} />
										)}
									</IconButton>
								</TableCell>
								<TableCell>
									{row.isTipping === "1" && (
										<HtmlTooltip
											placement="right"
											title={
												<React.Fragment>
													<Typography color="inherit" variant="body1" fontWeight={"500"}>
														Tombamento
													</Typography>
													<Typography color="inherit" variant="caption">
														Esta garantia é um tombamento.
													</Typography>
												</React.Fragment>
											}
										>
											<TIcon
												fontSize="small"
												sx={{
													ml: 0.8,
													cursor: "help",
													color: "#dd6d05",
													bgcolor: "#fae3b0",
													borderRadius: "50%",
													p: 0.2,
												}}
											/>
										</HtmlTooltip>
									)}
									{/* {row.pendingSetup && (
										<HtmlTooltip
											placement="right"
											title={
												<React.Fragment>
													<Typography color="inherit" variant="body1" fontWeight={"500"}>
														Taxa Setup
													</Typography>
													<Typography color="inherit" variant="caption">
														Esta garantia texe cobrança de taxa de setup. 
													</Typography>
												</React.Fragment>
											}
										>
											<AttachMoney
												fontSize="small"
												sx={{
													ml: 0.8,
													cursor: "help",
													color: "#6a05dd",
													bgcolor: "#e2d1fd",
													borderRadius: "50%",
													p: 0.2,
												}}
											/>
										</HtmlTooltip>
									)} */}
								</TableCell>

								<TableCell sx={{ textAlign: "center" }}>
									<Tooltip title="Clique aqui para abrir a garantia" placement="top-start">
										<IconButton
											size="small"
											onClick={() => window.open(`${URL}/garantias/visualizar/${row.id}`)}
											sx={{ borderRadius: "18px" }}
										>
											<Icon
												sx={{
													color: "#0170B3",
													fontWeight: "normal",
												}}
											>
												{" "}
												crop_free_OutlinedIcon
											</Icon>
											<Typography
												variant="subtitle2"
												marginLeft={1}
												sx={{
													color: "#0170B3",
													fontWeight: "normal",
												}}
											>
												Abrir
											</Typography>
										</IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						))}
				</TableBody>

				{totalCount === 0 && !isLoading && <caption>{Environment.LISTAGEM_VAZIA}</caption>}
				<TableFooter />
			</Table>
			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={Math.max(pagina - 1, 0)}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={[10, 25, 50]}
					onPageChange={(_, newPage) =>
						setSearchParams(
							{
								busca,
								pagina: (newPage + 1).toString(),
								tipo: typeSearch,
								status: statusProposal,
								origem: origemProposal,
								dataini: dataIniProposal,
								datafim: dataFimProposal,
								clientFilter: clientFilter,
								managerId: managerId,
								tombamento: tombamento,
								limit,
							},
							{ replace: true }
						)
					}
					onRowsPerPageChange={(event) =>
						setSearchParams(
							{
								busca,
								pagina: "1",
								tipo: typeSearch,
								status: statusProposal,
								origem: origemProposal,
								dataini: dataIniProposal,
								datafim: dataFimProposal,
								clientFilter: clientFilter,
								managerId: managerId,
								tombamento: tombamento,
								limit: event.target.value,
							},
							{ replace: true }
						)
					}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer>
	);
};

export default TableProposal;
